<script setup lang="ts">
import type { ProductDetail, ProductLineDetail } from '~/types/api'
import { addFilter } from '~/utils/APIFilters'

const head = useLocaleHead()

useServerHead({
  htmlAttrs: head.value.htmlAttrs,
  link: head.value.link,
  meta: head.value.meta,
})

const route = useRoute()
const { line: lineSlug, slug: setSlug } = route.params as {
  line: string
  slug: string
}
const { locale, t } = useI18n()

const productSetsRepository = createProductSetsRepository()
const productLineRepository = createProductLineRepository()

const { data: setDetail, status: setDetailStatus } = await useAsyncData(
  'productSet_' + setSlug + '_' + locale.value,
  // TODO: Locale should be handled by getBySlug method - for now using 'CZ_cs' as a workaround for sets without translations
  () => productSetsRepository.getBySlug(lineSlug, setSlug)
)

const productLine = ref<null | ProductLineDetail>(null)
if (setDetail.value && setDetail.value.productLine) {
  const { data: productLineData } = await useAsyncData(
    'productLine_' + setDetail.value?.productLine?.title,
    () => productLineRepository.getByNid(setDetail.value!.productLine!.nid)
  )

  watchEffect(() => {
    if (setDetailStatus.value === 'success') {
      productLine.value = productLineData.value
    }
  })
}

function throwUnknownSetError() {
  throw createError({
    statusCode: 404,
    message: t('error.productSet.notFound', [setSlug, titleCase(lineSlug)]),
    fatal: true,
  })
}

watchEffect(() => {
  if (setDetailStatus.value === 'error') {
    throwUnknownSetError()
  }
})

const productFilter = addFilter('productSet.nid', setDetail.value?.nid)
const { isLoggedIn, userCustomerGroupId } = storeToRefs(useUser())

const { customerGroupId, priceCategoryId } = storeToRefs(useUserConfig())

const { data: productsData, status: productsStatus } = await useAsyncData(
  () =>
    createProductsRepository().getAllProductsDetail({
      filter: APIFilters.makeFilter(productFilter),
      ...((customerGroupId?.value || userCustomerGroupId.value) && {
        customerGroupNid: customerGroupId.value ?? userCustomerGroupId.value,
      }),
      ...(priceCategoryId?.value && {
        priceCategoryNid: priceCategoryId.value,
      }),
    }),
  { watch: [isLoggedIn, customerGroupId, priceCategoryId] }
)

const products = computed(() => productsData.value?.items ?? [])

watch(
  products,
  () => {
    if (productsStatus.value === 'success' && !products.value.length) {
      // TODO: Probably not using the correct error message
      throwUnknownSetError()
    }
  },
  { immediate: true }
)
const selectedProduct = ref<ProductDetail | undefined>(products.value[0])

const prebuildConfigurationNid = ref<number | undefined>(undefined)
const forceFetchKey = ref<number | undefined>(undefined)

function switchToPrebuildConfiguration(configurationNid: number) {
  if (prebuildConfigurationNid.value === configurationNid) {
    forceFetchKey.value = Date.now()
  } else {
    prebuildConfigurationNid.value = configurationNid
  }
  navigateTo({ path: route.fullPath, hash: '#configurator' })
}
const { trimDescription, addSuffix } = useMeta()
const { defaultOgImage } = useOgImage()

useSeoMeta({
  title:
    addSuffix(productLine.value?.title + ' ' + setDetail.value?.title) ??
    t('pages.products.meta.title'),
  // TODO: Should use the product set meta description
  description:
    trimDescription(stripHtml(productLine.value?.metaDescription || '')) ??
    t('pages.index.meta.description'),
  ogImage: setDetail.value?.images[0]?.url
    ? withHost(setDetail.value?.images[0]?.url) + '/1200x1200/FIT' // TODO: Not great image, would be better if LD Seating added banner images in admin
    : defaultOgImage,
})

// TODO: Should use just one API call that returns all the data needed for the page
// Right now, the page makes multiple API calls to get the data and is blocking in SPA
// Only thing missing now are product details in the response

const productDetailsRef = useTemplateRef('productDetails')
const configuratorRef = useTemplateRef('configurator')
const filesRef = useTemplateRef('files')
const productDetailsIsVisible = useElementVisibility(productDetailsRef, {
  rootMargin: '-20% 0px',
})
const configuratorIsVisible = useElementVisibility(configuratorRef)
const filesIsVisible = useElementVisibility(filesRef, {
  rootMargin: '-20% 0px',
})

// TODO: Add fallback to configurator without 3D view - basically move this detection to the configurator component
// function detectWebGLContext() {
//   const canvas = document.createElement('canvas')

//   const gl =
//     canvas.getContext('webgl') || canvas.getContext('experimental-webgl')

//   return gl instanceof WebGLRenderingContext
// }

// const isWebGLAvailable = ref(false)

// onMounted(() => {
//   isWebGLAvailable.value = detectWebGLContext()
// })

const { sendProductSetView } = useEvent()

sendProductSetView(productLine.value?.title + ' ' + setDetail.value?.title)
</script>

<template>
  <div id="setContent">
    <!-- TODO: Add skeletons -->
    <ProductHero
      :id="productLine?.title + '_' + setDetail?.title"
      :line="productLine"
      :set="setDetail"
      @configure-from-image="switchToPrebuildConfiguration"
    />
    <ProductSetTabMenu
      :set-name="productLine?.title + '_' + setDetail?.title"
      :has-configurator="setDetail?.readyForConfigurator"
      class="sticky top-0 z-40"
    />
    <ProductLinesProductLineWidget
      v-for="widget in setDetail?.widgets"
      :key="widget.nid"
      :widget="widget"
    />
    <div ref="productDetails">
      <ProductProperties
        v-if="selectedProduct"
        id="parameters"
        :set="setDetail"
        :products="products"
        :line="productLine"
        :selected-product="selectedProduct"
        @update:selected-product="
          (product: ProductDetail) => (selectedProduct = product)
        "
      />
    </div>
    <div id="configurator" ref="configurator">
      <NuxtErrorBoundary>
        <ProductConfigurator
          v-if="setDetail?.readyForConfigurator"
          :configurator-is-visible="configuratorIsVisible"
          :product-details-is-visible="productDetailsIsVisible"
          :files-is-visible="filesIsVisible"
          :product="selectedProduct"
          :product-nid="selectedProduct?.nid"
          :product-set-nid="setDetail?.nid"
          :configuration-nid="prebuildConfigurationNid"
          :force-fetch-key="forceFetchKey"
          :image-placeholder="setDetail?.products[0]?.images[0]"
        />
        <template #error="{ clearError }">
          <div>
            <div class="container pt-20">
              <p class="font-pragmatica-ext mb-10 text-4xl font-bold">
                {{ $t('error.somethingWentWrong') }}
              </p>
              <UiButtonBlack @click="clearError">
                {{ $t('base.back') }}
              </UiButtonBlack>
            </div>
            <ProductSetsGrid
              id="variants"
              :products="products"
              :product-line-title="productLine?.title"
            />
          </div>
        </template>
      </NuxtErrorBoundary>
    </div>
    <ProductSetsGrid
      v-if="!setDetail?.readyForConfigurator"
      id="variants"
      :products="products"
      :product-line-title="productLine?.title"
    />
    <div ref="files">
      <ProductDownload
        id="files"
        :line-id="productLine?.translationOfNid"
        :set-nid="setDetail?.nid"
      />
    </div>
    <ProductGallery
      id="gallery"
      :line="productLine"
      :set-nid="setDetail?.nid"
    />
    <ProductReferences
      id="references"
      :set-nid="setDetail?.nid"
      :line="productLine"
    />
    <ProductSurfaces
      id="surfaces"
      :set-nid="setDetail?.nid"
      :products="products"
      :selected-product="selectedProduct"
      @update:selected-product="
        (product: ProductDetail) => (selectedProduct = product)
      "
    />
    <ProductAccessories id="accessories" :set-nid="setDetail?.nid" />
    <ProductSmartFilters
      id="information"
      :set-nid="setDetail?.nid"
      :line="productLine"
    />
    <ProductFooter :line="productLine" :set="setDetail" />
  </div>
</template>
